'use client';

import { useCallback, useMemo, useState } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  Text,
  BodyDefault,
  Scroll,
  ImageWithSkeleton,
  IconButton,
  Tooltip,
  LegacyDialog,
} from '@terra/ui-design-system';
import { Maximize02Icon, Edit03Icon, Cancel01Icon } from '@terra/ui-icons';
import { normalizeHttpHeaders } from '@terra/shared-utils';
import type { WebPageWithSignedScreenshotReturnDto } from '@terra/shared-contracts-web-pages';
import { useTranslations } from 'next-intl';
import { ReplaceWebpageScreenshotDialog } from './ReplaceWebpageScreenshotDialog';

type WebPageDetailsSectionProps = {
  webpageData: WebPageWithSignedScreenshotReturnDto;
  onScreenshotReplaced?: () => void | Promise<void>;
};

export function WebPageDetailsSection({
  webpageData,
  onScreenshotReplaced,
}: WebPageDetailsSectionProps) {
  const tCommon = useTranslations('designSystem.common');
  const tAssets = useTranslations('designSystem.applicationAssets');
  const editLabel = tCommon('edit');
  const [isScreenshotPreviewOpen, setIsScreenshotPreviewOpen] = useState(false);
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false);
  const handleScreenshotPreviewOpen = useCallback(() => {
    setIsScreenshotPreviewOpen(true);
  }, []);
  const handleScreenshotPreviewChange = useCallback(
    (details: { open: boolean }) => {
      setIsScreenshotPreviewOpen(details.open);
    },
    [],
  );
  const totalLinks =
    (webpageData.linksInternal?.length || 0) +
    (webpageData.linksExternal?.length || 0);
  const hasCookies = webpageData.cookies && webpageData.cookies.length > 0;
  const displayHeaders = useMemo(
    () =>
      normalizeHttpHeaders(
        (webpageData.headers ?? []).map((header) => ({
          name: header.key,
          value: header.value,
        })),
      ),
    [webpageData.headers],
  );
  const hasHeaders = displayHeaders.length > 0;

  return (
    <Scroll orientation="vertical" h="full">
      <VStack gap={6} p={6} alignItems="stretch">
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={3}>
            {tAssets('sections.screenshot')}
          </Text>
          {webpageData.signedUrl ? (
            <>
              <Box
                borderRadius="md"
                overflow="hidden"
                borderWidth={1}
                borderColor="border.subtle"
                bg="bg.default"
                maxH="500px"
                cursor="pointer"
                onClick={handleScreenshotPreviewOpen}
                role="button"
                aria-label="Open screenshot preview"
                position="relative"
                _hover={{ borderColor: 'border.default' }}
              >
                <ImageWithSkeleton
                  src={webpageData.signedUrl}
                  alt={`Screenshot of ${webpageData.url}`}
                  errorMessage="Failed to load screenshot"
                  objectFit="contain"
                />
                <HStack
                  position="absolute"
                  top="2"
                  right="2"
                  gap="1"
                  alignItems="center"
                >
                  <Tooltip title={editLabel}>
                    <Box
                      bg="bg.subtle"
                      borderRadius="sm"
                      p="1"
                      borderWidth={1}
                      borderColor="border.subtle"
                      color="fg.muted"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      role="button"
                      aria-label={editLabel}
                      onClick={(event) => {
                        event.stopPropagation();
                        setIsReplaceDialogOpen(true);
                      }}
                    >
                      <Edit03Icon size={12} strokeWidth={1.5} />
                    </Box>
                  </Tooltip>
                  <Tooltip title="View full screen">
                    <Box
                      bg="bg.subtle"
                      borderRadius="sm"
                      p="1"
                      borderWidth={1}
                      borderColor="border.subtle"
                      color="fg.muted"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Maximize02Icon size={12} strokeWidth={1.5} />
                    </Box>
                  </Tooltip>
                </HStack>
              </Box>

              <LegacyDialog.Root
                open={isScreenshotPreviewOpen}
                onOpenChange={handleScreenshotPreviewChange}
                lazyMount
                unmountOnExit
              >
                <LegacyDialog.Backdrop />
                <LegacyDialog.Positioner>
                  <LegacyDialog.Content
                    w="95vw"
                    h="95vh"
                    maxW="95vw"
                    maxH="95vh"
                    p="0"
                    display="flex"
                    flexDirection="column"
                  >
                    <HStack
                      justifyContent="space-between"
                      alignItems="center"
                      px="3"
                      py="2"
                      borderBottomWidth={1}
                      borderColor="border.subtle"
                    >
                      <LegacyDialog.Title>
                        Screenshot Preview
                      </LegacyDialog.Title>
                      <LegacyDialog.CloseTrigger asChild>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          aria-label="Close screenshot preview"
                        >
                          <Cancel01Icon size={16} />
                        </IconButton>
                      </LegacyDialog.CloseTrigger>
                    </HStack>

                    <Box
                      flex="1"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      overflow="auto"
                      p="3"
                      bg="bg.canvas"
                    >
                      <ImageWithSkeleton
                        src={webpageData.signedUrl}
                        alt={`Full screenshot of ${webpageData.url}`}
                        errorMessage="Failed to load screenshot"
                        objectFit="contain"
                      />
                    </Box>
                  </LegacyDialog.Content>
                </LegacyDialog.Positioner>
              </LegacyDialog.Root>
            </>
          ) : (
            <Box
              borderRadius="md"
              borderWidth={1}
              borderColor="border.subtle"
              bg="bg.subtle"
              p={8}
              minH="160px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
            >
              <Tooltip title={editLabel}>
                <Box
                  position="absolute"
                  top="2"
                  right="2"
                  bg="bg.subtle"
                  borderRadius="sm"
                  p="1"
                  borderWidth={1}
                  borderColor="border.subtle"
                  color="fg.muted"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  role="button"
                  aria-label={editLabel}
                  onClick={() => setIsReplaceDialogOpen(true)}
                >
                  <Edit03Icon size={12} strokeWidth={1.5} />
                </Box>
              </Tooltip>
              <BodyDefault color="fg.muted">
                {tAssets('drawer.features.imageUnavailable')}
              </BodyDefault>
            </Box>
          )}
          <ReplaceWebpageScreenshotDialog
            open={isReplaceDialogOpen}
            webpageId={webpageData.id}
            onOpenChange={setIsReplaceDialogOpen}
            onScreenshotReplaced={onScreenshotReplaced}
          />
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={2}>
            URL
          </Text>
          <Text
            fontSize="sm"
            fontFamily="mono"
            wordBreak="break-all"
            bg="bg.subtle"
            p={3}
            borderRadius="md"
          >
            {webpageData.url}
          </Text>
        </Box>

        {webpageData.description && (
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={2}>
              Description
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {webpageData.description}
            </Text>
          </Box>
        )}

        {totalLinks > 0 && (
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={3}>
              Links ({totalLinks})
            </Text>
            <VStack gap={4} alignItems="stretch">
              {webpageData.linksInternal &&
                webpageData.linksInternal.length > 0 && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="fg.muted"
                      mb={2}
                    >
                      Internal Links ({webpageData.linksInternal.length})
                    </Text>
                    <Box
                      maxH="200px"
                      overflowY="auto"
                      bg="bg.subtle"
                      p={3}
                      borderRadius="md"
                    >
                      <VStack gap={1} alignItems="stretch">
                        {webpageData.linksInternal.map((link, index) => (
                          <Text
                            key={index}
                            fontSize="xs"
                            fontFamily="mono"
                            wordBreak="break-all"
                            color="fg.default"
                          >
                            {link}
                          </Text>
                        ))}
                      </VStack>
                    </Box>
                  </Box>
                )}

              {webpageData.linksExternal &&
                webpageData.linksExternal.length > 0 && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="fg.muted"
                      mb={2}
                    >
                      External Links ({webpageData.linksExternal.length})
                    </Text>
                    <Box
                      maxH="200px"
                      overflowY="auto"
                      bg="bg.subtle"
                      p={3}
                      borderRadius="md"
                    >
                      <VStack gap={1} alignItems="stretch">
                        {webpageData.linksExternal.map((link, index) => (
                          <Text
                            key={index}
                            fontSize="xs"
                            fontFamily="mono"
                            wordBreak="break-all"
                            color="fg.muted"
                          >
                            {link}
                          </Text>
                        ))}
                      </VStack>
                    </Box>
                  </Box>
                )}
            </VStack>
          </Box>
        )}

        {hasCookies && (
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={3}>
              Cookies ({webpageData.cookies?.length || 0})
            </Text>
            <Box
              maxH="300px"
              overflowY="auto"
              bg="bg.subtle"
              p={3}
              borderRadius="md"
            >
              <VStack gap={2} alignItems="stretch">
                {webpageData.cookies?.map((cookie, index) => (
                  <Box
                    key={index}
                    p={3}
                    bg="bg.canvas"
                    borderRadius="md"
                    borderWidth={1}
                    borderColor="border.subtle"
                  >
                    <HStack gap={2} mb={1}>
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        color="fg.default"
                      >
                        {cookie.name}:
                      </Text>
                      <Text
                        fontSize="xs"
                        fontFamily="mono"
                        wordBreak="break-all"
                        flex={1}
                        color="fg.muted"
                      >
                        {cookie.value}
                      </Text>
                    </HStack>
                    {(cookie.domain || cookie.path || cookie.expires) && (
                      <HStack gap={4} fontSize="2xs" color="fg.muted" mt={2}>
                        {cookie.domain && <Text>Domain: {cookie.domain}</Text>}
                        {cookie.path && <Text>Path: {cookie.path}</Text>}
                        {cookie.expires && (
                          <Text>Expires: {cookie.expires}</Text>
                        )}
                      </HStack>
                    )}
                  </Box>
                ))}
              </VStack>
            </Box>
          </Box>
        )}

        {hasHeaders && (
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={3}>
              Response Headers ({displayHeaders.length})
            </Text>
            <Box
              maxH="300px"
              overflowY="auto"
              bg="bg.subtle"
              p={3}
              borderRadius="md"
            >
              <VStack gap={1} alignItems="stretch">
                {displayHeaders.map((header, index) => (
                  <HStack key={index} gap={2} fontSize="xs">
                    <Text fontWeight="medium" color="fg.default" minW="150px">
                      {header.name}:
                    </Text>
                    <Text
                      fontFamily="mono"
                      wordBreak="break-all"
                      flex={1}
                      color="fg.muted"
                    >
                      {header.value}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </Box>
        )}

        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={3}>
            Metadata
          </Text>
          <VStack gap={2} alignItems="stretch">
            {webpageData.role && (
              <HStack gap={2}>
                <Text fontSize="xs" color="fg.muted" minW="100px">
                  User Role:
                </Text>
                <Text fontSize="xs" fontFamily="mono">
                  {webpageData.role}
                </Text>
              </HStack>
            )}
            <HStack gap={2}>
              <Text fontSize="xs" color="fg.muted" minW="100px">
                Page ID:
              </Text>
              <Text fontSize="xs" fontFamily="mono">
                {webpageData.id}
              </Text>
            </HStack>
            {webpageData.createdAt && (
              <HStack gap={2}>
                <Text fontSize="xs" color="fg.muted" minW="100px">
                  Discovered:
                </Text>
                <Text fontSize="xs">
                  {new Date(webpageData.createdAt).toLocaleString()}
                </Text>
              </HStack>
            )}
            {webpageData.updatedAt && (
              <HStack gap={2}>
                <Text fontSize="xs" color="fg.muted" minW="100px">
                  Last Updated:
                </Text>
                <Text fontSize="xs">
                  {new Date(webpageData.updatedAt).toLocaleString()}
                </Text>
              </HStack>
            )}
          </VStack>
        </Box>
      </VStack>
    </Scroll>
  );
}
